import React from "react";
import { Link, useParams } from "react-router-dom";
import IndexGist from "./gist/IndexGist";

const Home = () => {
  const { type, id } = useParams<{ type?: string; title?: string; id?: string }>();
  const isGistRoute = Boolean(type && id);

  if (isGistRoute) {
    return (
      <div
        className="flex flex-row w-full min-h-screen overflow-hidden"
        style={{ backgroundColor: "var(--color-ivory)" }}
      >
        <div
          className="w-full mt-14 flex justify-center overflow-auto pb-12"
          style={{ fontFamily: "var(--font-primary)" }}
        >
          <IndexGist />
        </div>
      </div>
    );
  }

  const workflowCards = [
    {
      eyebrow: "Step 1",
      title: "Choose a subject",
      copy:
        "Start with the Subject Tree. It gives you the map of the knowledge space before you open individual gists.",
    },
    {
      eyebrow: "Step 2",
      title: "Read a gist",
      copy:
        "A gist is a structured idea unit: title, summary, and body. It is smaller than an article but richer than a comment.",
    },
    {
      eyebrow: "Step 3",
      title: "Follow the discussion",
      copy:
        "Use Reader View for linear reading and Discussion View when you want to see how replies and arguments connect.",
    },
  ];

  return (
    <main className="home-landing-page" aria-label="Gistoscope home">
      <section className="home-hero-section">
        <div className="home-hero-copy">
          <div className="home-kicker">Structured knowledge navigation</div>
          <h1 className="home-hero-title">Gistoscope</h1>
          <p className="home-hero-lede">
            Gistoscope is a place to read, write, and explore connected ideas. It organizes
            knowledge as gists: compact thought units that can be grouped into subjects,
            replies, discussions, and argument trees.
          </p>

          <div className="home-hero-actions" aria-label="Primary navigation">
            <Link to="/" className="home-action-primary">
              Explore Subject Tree
            </Link>
            <a href="#how-gistoscope-works" className="home-action-secondary">
              How it works
            </a>
          </div>
        </div>

        <aside className="home-hero-card" aria-label="Gistoscope quick idea">
          <div className="home-hero-card-label">What is a gist?</div>
          <div className="home-gist-preview-card">
            <div className="home-gist-preview-title">Title</div>
            <div className="home-gist-preview-summary">
              The core claim or idea in one clear sentence.
            </div>
            <div className="home-gist-preview-body">
              The body explains the idea, gives context, and connects it to the larger
              discussion.
            </div>
          </div>
          <p className="home-hero-card-note">
            Think of a gist as a focused building block for knowledge, not just a post.
          </p>
        </aside>
      </section>

      <section className="home-nav-grid" aria-label="Quick navigation">
        <Link to="/" className="home-nav-card home-nav-card-primary">
          <span className="home-nav-card-kicker">Start here</span>
          <span className="home-nav-card-title">Subject Tree</span>
          <span className="home-nav-card-copy">
            Browse the map of subjects and choose where you want to enter the knowledge space.
          </span>
        </Link>

        <a href="#reader-and-discussion" className="home-nav-card">
          <span className="home-nav-card-kicker">Read better</span>
          <span className="home-nav-card-title">Reader + Discussion Views</span>
          <span className="home-nav-card-copy">
            Learn when to read a thread linearly and when to inspect the structure of replies.
          </span>
        </a>

        <a href="#what-makes-it-different" className="home-nav-card">
          <span className="home-nav-card-kicker">Why it exists</span>
          <span className="home-nav-card-title">Connected ideas</span>
          <span className="home-nav-card-copy">
            See how Gistoscope differs from a normal blog, forum, or comment feed.
          </span>
        </a>
      </section>

      <section id="how-gistoscope-works" className="home-section">
        <div className="home-section-heading">
          <div className="home-kicker">How to use it</div>
          <h2>Start with the map, then follow the ideas.</h2>
          <p>
            Gistoscope is designed for moving between overview and detail. You can begin with
            the tree, open a subject, read individual gists, and then follow replies as a
            discussion structure.
          </p>
        </div>

        <div className="home-workflow-grid">
          {workflowCards.map((card) => (
            <article key={card.title} className="home-workflow-card">
              <div className="home-workflow-eyebrow">{card.eyebrow}</div>
              <h3>{card.title}</h3>
              <p>{card.copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="reader-and-discussion" className="home-section home-split-section">
        <div className="home-section-heading home-section-heading-compact">
          <div className="home-kicker">Two reading modes</div>
          <h2>Use the right view for the job.</h2>
          <p>
            The same material can be read as a sequence or inspected as a structure. Both are
            useful, depending on what you are trying to understand.
          </p>
        </div>

        <div className="home-mode-grid">
          <article className="home-mode-card">
            <div className="home-mode-label">Reader View</div>
            <h3>Read a thread like a developing explanation.</h3>
            <p>
              Reader View is best when you want to move through gists as a readable sequence
              and keep nearby context on screen.
            </p>
          </article>

          <article className="home-mode-card">
            <div className="home-mode-label">Discussion View</div>
            <h3>See the shape of the conversation.</h3>
            <p>
              Discussion View is best when the structure matters: parent ideas, replies,
              branches, and competing arguments.
            </p>
          </article>
        </div>
      </section>

      <section id="what-makes-it-different" className="home-section home-closing-section">
        <div>
          <div className="home-kicker">A different kind of forum</div>
          <h2>Not just posts. Not just comments. A map of connected thought.</h2>
          <p>
            Gistoscope treats ideas as durable objects. A subject gives the place, a gist gives
            the idea, and a reply gives the connection. That makes it easier to return later,
            continue a discussion, and understand how one point leads to another.
          </p>
        </div>
        <Link to="/" className="home-action-primary home-closing-action">
          Open Subject Tree
        </Link>
      </section>
    </main>
  );
};

export default Home;
